import { useNotification } from "hooks/useNotification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import instance from "./axiosInstance";
import { Vehicle, VehicleDTO } from "types/vehicle.type";

export const ENDPOINT = "/vehicle";
export enum VehicleQueryKey {
  GetAll = "vehicle-get-all",
  GetOne = "vehicle-get-one",
  GetContentByCode = "vehicle-get-content-by-code",
}

export const useVehicles = () =>
  useQuery({
    queryKey: [VehicleQueryKey.GetAll],
    queryFn: async (): Promise<Vehicle[]> => {
      const response = await instance.get(ENDPOINT);
      return response.data;
    },
  });

export const useVehicle = (id: number) =>
  useQuery({
    queryKey: [VehicleQueryKey.GetOne, id],
    queryFn: async (): Promise<Vehicle> => {
      const response = await instance.get(`${ENDPOINT}/${id}`);
      return response.data;
    },
  });

export const useVehicleByCode = (code: string) =>
  useQuery({
    queryKey: [VehicleQueryKey.GetContentByCode, code],
    queryFn: async (): Promise<VehicleByCodeResponse[]> => {
      const response = await instance.get(
        `${ENDPOINT}/vehicle-content/${code}`,
      );
      return response.data;
    },
    retry: 1,
  });

export type VehicleByCodeResponse = {
  registrationNumber: string;
  wasteList: {
    code: string;
    description: string;
    quantity: string;
  }[];
};

export const useCreateVehicle = () => {
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: VehicleDTO): Promise<Vehicle> => {
      const response = await instance.post(ENDPOINT, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [VehicleQueryKey.GetAll],
      });
      notification.info(`Fordon skapat`);
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const deleteVehicle = () => ({
  mutationFn: async (id: number): Promise<void> => {
    await instance.delete(`${ENDPOINT}/${id}`);
  },
});

export const useUpdateVehicle = (id?: number) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: VehicleDTO): Promise<Vehicle> => {
      const response = await instance.put(`${ENDPOINT}/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [VehicleQueryKey.GetAll],
      });
      notification.info(`Fordon ändrat`);
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};
